import { useEffect, useId, useRef, useState } from "react";

import { CalendarIcon } from "@radix-ui/react-icons";
import { differenceInCalendarDays, format, isValid } from "date-fns";
import { useAtom } from "jotai";
import { ArrowRight, Check } from "lucide-react";

import { ComparisonRangePicker } from "src/components/DateRangePicker/ComparisonRangePicker";
import { ReportingIntervalPicker } from "src/components/DateRangePicker/ReportingIntervalPicker";
import { Button } from "src/components/ui/button";
import { Calendar } from "src/components/ui/calendar";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "src/components/ui/drawer";
import { Input } from "src/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  getCalendarMonthForRangeEnd,
  getComparisonDates,
  getLastDaysRange,
  getLastMonthRange,
  getLastMonthsRange,
  getLastWeekRange,
  getThisMonthRange,
  getThisWeekRange,
} from "src/lib/date";
import { useIsMobile } from "src/lib/hooks/useMobile";
import { parseReportDate } from "src/lib/parseReportDate";
import {
  reportingIntervalAtom,
  reportingIntervals,
} from "src/lib/reportingInterval";
import { cn } from "src/lib/utils";

const rangePresets = [
  { name: "This week", getValue: getThisWeekRange },
  { name: "This month", getValue: getThisMonthRange },
  { name: "Last week", getValue: getLastWeekRange },
  { name: "Last month", getValue: getLastMonthRange },
  {
    name: "Last 30 days",
    getValue: (min, max) => getLastDaysRange(30, min, max),
  },
  {
    name: "Last 90 days",
    getValue: (min, max) => getLastDaysRange(90, min, max),
  },
  {
    name: "Last 6 months",
    getValue: (min, max) => getLastMonthsRange(6, min, max),
  },
  {
    name: "Last 12 months",
    getValue: (min, max) => getLastMonthsRange(12, min, max),
  },
];
const dateValue = (date) =>
  date && isValid(date) ? format(date, "yyyy-MM-dd") : "";

/** Stages report filters locally; only Apply commits them and refreshes reports. */
export function DateRangePicker({
  atom,
  onSubmit,
  setStore: externalSetStore,
  className,
  useComparison = false,
  useReportingInterval = false,
}) {
  const [stored, setAtomStore] = useAtom(atom);
  const setStore = externalSetStore || setAtomStore;
  const [reportingInterval, setReportingInterval] = useAtom(
    reportingIntervalAtom
  );
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const [calendarMonth, setCalendarMonth] = useState(() =>
    getCalendarMonthForRangeEnd(stored.dateTo)
  );
  const pendingSubmit = useRef(false);
  const id = useId();
  const isMobile = useIsMobile();
  const { dateMin, dateMax, loading } = stored;
  const dateFrom = parseReportDate(draft.from);
  const dateTo = parseReportDate(draft.to);
  const validRange =
    isValid(dateFrom) &&
    isValid(dateTo) &&
    dateFrom <= dateTo &&
    (!dateMin || dateValue(dateFrom) >= dateValue(dateMin)) &&
    (!dateMax || dateValue(dateTo) <= dateValue(dateMax));
  const comparison = validRange
    ? getComparisonDates(
        dateFrom,
        dateTo,
        draft.comparisonMode,
        dateMin,
        dateMax
      )
    : { compareFrom: null, compareTo: null };
  const committedComparison = getComparisonDates(
    stored.dateFrom,
    stored.dateTo,
    stored.comparisonMode,
    dateMin,
    dateMax
  );
  const disabledComparisonModes = ["previousPeriod", "previousYear"].filter(
    (mode) =>
      !validRange ||
      !getComparisonDates(dateFrom, dateTo, mode, dateMin, dateMax).compareFrom
  );
  const effectiveComparisonMode = disabledComparisonModes.includes(
    draft.comparisonMode
  )
    ? "noComparison"
    : draft.comparisonMode || "noComparison";

  // Run after atom updates so the parent's refresh callback sees the applied dates.
  useEffect(() => {
    if (!pendingSubmit.current) return;
    pendingSubmit.current = false;
    onSubmit?.();
  }, [stored.dateFrom, stored.dateTo, stored.comparisonMode, onSubmit]);

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      setDraft({
        from: dateValue(stored.dateFrom),
        to: dateValue(stored.dateTo),
        comparisonMode: stored.comparisonMode || "noComparison",
        interval: reportingInterval,
      });
      setCalendarMonth(
        getCalendarMonthForRangeEnd(stored.dateTo || stored.dateFrom)
      );
    }
    setOpen(nextOpen);
  };

  const apply = () => {
    if (!validRange || loading) return;
    const datesChanged =
      draft.from !== dateValue(stored.dateFrom) ||
      draft.to !== dateValue(stored.dateTo) ||
      effectiveComparisonMode !== (stored.comparisonMode || "noComparison");
    if (datesChanged) {
      pendingSubmit.current = true;
      setStore((previous) => ({
        ...previous,
        dateFrom,
        dateTo,
        comparisonMode: effectiveComparisonMode,
      }));
    }
    if (useReportingInterval) setReportingInterval(draft.interval);
    setOpen(false);
  };

  const triggerButton = (
    <Button
      id="date-range-picker"
      variant="outline"
      size="sm"
      className={cn(
        "h-auto min-h-8 max-w-full justify-start px-3 py-1.5 text-left font-normal",
        className
      )}
    >
      <CalendarIcon className="mt-0.5 size-4 shrink-0 self-start" />
      <span className="flex min-w-0 flex-col items-start gap-x-3 gap-y-1 md:flex-row md:flex-wrap md:items-center">
        <span>
          {stored.dateFrom
            ? `${format(stored.dateFrom, "LLL dd, y")}${stored.dateTo ? ` - ${format(stored.dateTo, "LLL dd, y")}` : ""}`
            : "Choose dates"}
        </span>
        <span className="grid w-full grid-flow-col grid-cols-[minmax(0,1fr)_auto] items-start gap-2 text-xs text-muted-foreground empty:hidden md:w-auto">
          {useComparison &&
            committedComparison.compareFrom &&
            committedComparison.compareTo && (
              <span className="whitespace-normal">
                vs. {format(committedComparison.compareFrom, "MMM d, yyyy")} -{" "}
                {format(committedComparison.compareTo, "MMM d, yyyy")}
              </span>
            )}
          {useReportingInterval && reportingInterval !== "auto" && (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              {
                reportingIntervals.find(
                  ({ value }) => value === reportingInterval
                )?.label
              }
            </span>
          )}
        </span>
      </span>
    </Button>
  );

  const presets = (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Quick ranges
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1 md:grid-cols-1">
        {rangePresets.map((preset) => {
          const range = preset.getValue(dateMin, dateMax);
          const selected =
            draft.from === dateValue(range.from) &&
            draft.to === dateValue(range.to);
          return (
            <Button
              key={preset.name}
              variant="ghost"
              size="sm"
              aria-pressed={selected}
              className={cn(
                "justify-between px-2.5 font-normal",
                selected &&
                  "bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"
              )}
              onClick={() => {
                setDraft((prev) => ({
                  ...prev,
                  from: dateValue(range.from),
                  to: dateValue(range.to),
                }));
                setCalendarMonth(getCalendarMonthForRangeEnd(range.to));
              }}
            >
              {preset.name}
              {selected && <Check className="size-3.5" />}
            </Button>
          );
        })}
      </div>
    </div>
  );

  const selectedPreset = rangePresets.find((preset) => {
    const range = preset.getValue(dateMin, dateMax);
    return (
      draft.from === dateValue(range.from) && draft.to === dateValue(range.to)
    );
  });
  const mobilePresets = (
    <Select
      value={selectedPreset?.name || "custom"}
      onValueChange={(name) => {
        const preset = rangePresets.find((preset) => preset.name === name);
        if (!preset) return;
        const range = preset.getValue(dateMin, dateMax);
        setDraft((prev) => ({
          ...prev,
          from: dateValue(range.from),
          to: dateValue(range.to),
        }));
        setCalendarMonth(getCalendarMonthForRangeEnd(range.to));
      }}
    >
      <SelectTrigger className="w-full" aria-label="Quick range">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="custom">Custom range</SelectItem>
        {rangePresets.map(({ name }) => (
          <SelectItem key={name} value={name}>
            {name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const normalizeDateField = (field) => {
    const date = parseReportDate(draft[field]);
    if (isValid(date)) {
      setDraft((prev) => ({ ...prev, [field]: dateValue(date) }));
      setCalendarMonth(getCalendarMonthForRangeEnd(date));
    }
  };

  const dateFields = (
    <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
      {["from", "to"].map((field, index) => (
        <span key={field} className="contents">
          {index === 1 && (
            <ArrowRight className="mb-2.5 size-4 text-muted-foreground" />
          )}
          <div className="min-w-0 space-y-1.5">
            <label
              htmlFor={`${id}-${field}`}
              className="text-xs font-medium text-muted-foreground"
            >
              {field === "from" ? "Start date" : "End date"}
            </label>
            <Input
              id={`${id}-${field}`}
              type="text"
              placeholder="YYYY-MM-DD"
              maxLength={100}
              onFocus={(event) => event.currentTarget.select()}
              onBlur={() => normalizeDateField(field)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  normalizeDateField(field);
                }
              }}
              value={draft[field] || ""}
              onChange={(e) => {
                const value = e.target.value;
                setDraft((prev) => ({ ...prev, [field]: value }));
                if (isValid(parseReportDate(value)))
                  setCalendarMonth(
                    getCalendarMonthForRangeEnd(parseReportDate(value))
                  );
              }}
            />
          </div>
        </span>
      ))}
    </div>
  );

  const calendar = (
    <Calendar
      mode="range"
      selected={{
        from: isValid(dateFrom) ? dateFrom : undefined,
        to: isValid(dateTo) ? dateTo : undefined,
      }}
      month={calendarMonth}
      onMonthChange={setCalendarMonth}
      captionLayout="dropdown"
      disabled={{ before: dateMin, after: dateMax }}
      startMonth={dateMin}
      endMonth={dateMax}
      onSelect={(range) =>
        setDraft((prev) => ({
          ...prev,
          from: dateValue(range?.from),
          to: dateValue(range?.to),
        }))
      }
      numberOfMonths={isMobile ? 1 : 2}
      weekStartsOn={1}
      className="mx-auto p-0"
      classNames={{
        week: "mt-1 flex w-full [&:first-child]:justify-end",
        month: "flex w-full flex-col gap-3",
      }}
    />
  );

  const settings = (
    <div className="grid shrink-0 gap-4 border-t bg-muted/20 px-5 py-3 md:grid-cols-2">
      <div
        className={cn(
          "space-y-2",
          !useComparison && "opacity-50 [&_button:disabled]:opacity-100"
        )}
      >
        <span className="text-xs font-medium text-muted-foreground">
          Compare with
          {!useComparison
            ? " - Not available for this report"
            : comparison.compareFrom && comparison.compareTo
              ? `: ${format(comparison.compareFrom, "MMM d, yyyy")} - ${format(comparison.compareTo, "MMM d, yyyy")}`
              : ""}
        </span>
        <ComparisonRangePicker
          disabled={!useComparison || !validRange}
          disabledValues={disabledComparisonModes}
          value={useComparison ? effectiveComparisonMode : "noComparison"}
          setValue={(comparisonMode) =>
            setDraft((prev) => ({ ...prev, comparisonMode }))
          }
          className="w-full"
        />
      </div>
      <ReportingIntervalPicker
        value={draft.interval || "auto"}
        disabled={!useReportingInterval}
        onValueChange={(interval) =>
          setDraft((prev) => ({ ...prev, interval }))
        }
      />
    </div>
  );

  const footer = (
    <div className="flex shrink-0 items-center justify-between gap-3 border-t px-5 py-3">
      <p className="text-xs text-muted-foreground" role="status">
        {validRange
          ? `${differenceInCalendarDays(dateTo, dateFrom) + 1} days selected`
          : "Choose a valid start and end date."}
      </p>
      <div className="flex shrink-0 gap-2">
        <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button size="sm" disabled={!validRange || loading} onClick={apply}>
          Apply
        </Button>
      </div>
    </div>
  );

  const content = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex flex-col md:flex-row">
          <div className="border-b bg-muted/20 p-4 md:w-44 md:shrink-0 md:border-b-0 md:border-r">
            {isMobile ? mobilePresets : presets}
          </div>
          <div className="min-w-0 space-y-4 p-4">
            {dateFields}
            {calendar}
          </div>
        </div>
      </div>
      {settings}
      {footer}
    </>
  );

  return (
    <div className="relative">
      {isMobile ? (
        <Drawer open={open} onOpenChange={handleOpenChange}>
          <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
          <DrawerContent className="p-0 data-[vaul-drawer-direction=bottom]:mt-0 data-[vaul-drawer-direction=bottom]:max-h-[94dvh]">
            <DrawerHeader className="sr-only">
              <DrawerTitle>Report period</DrawerTitle>
              <DrawerDescription className="sr-only">
                Choose dates and how to view your reports.
              </DrawerDescription>
            </DrawerHeader>
            {content}
          </DrawerContent>
        </Drawer>
      ) : (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
          <PopoverContent
            align="start"
            className="flex w-auto max-w-[calc(100vw-2rem)] flex-col overflow-hidden p-0"
            style={{
              maxHeight: "var(--radix-popover-content-available-height)",
            }}
            collisionPadding={12}
            aria-label="Report period"
          >
            {content}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
